import React, { memo, useEffect, useMemo } from 'react';

import constants from 'constants/constants';
import produce from 'immer';
import { orderBy } from 'lodash';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import useHashmapConverter from 'hooks/useHashmapConverter';
import { useQueryState } from 'hooks/useQueryState';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';

import { useQueryActivityCodesByVersion } from 'api/queries/activityCodesByVersion.query';

import { IState } from 'components/common/GlobalFilterComponent/GlobalFilterComponent';
import classes from 'components/common/GlobalFilterComponent/GlobalFilterComponent.module.scss';
import { FilterLabels } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { MenuList } from 'components/common/MyCustomSelect/components/MenuList/MenuList';
import { OptionsWithExclude } from 'components/common/MyCustomSelect/components/OptionsWithExclude/OptionsWithExclude';

import { activityCodeMapperUID } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { ProgramStatusUrlParams } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramStatusSummaryReport/programStatusSummaryReportTypes';
import { Select } from 'components/DesignSystem/Select';

interface IActivityCodesFilterProps {
    methods?: UseFormReturn<any, any>;
    name: string;
    setState?: React.Dispatch<React.SetStateAction<IState>>;
    handleFilters?: () => void;
    overrideLatestVersionId?: number | undefined;
    skipFormSet?: boolean;
}
type Value = { name: number; isExclude: boolean; alias: string | null };

export const ActivityCodesFilter = memo(
    ({
        methods,
        name,
        setState,
        handleFilters,
        overrideLatestVersionId,
        skipFormSet = false,
    }: IActivityCodesFilterProps) => {
        //get version hashes from url params
        const { id, versionToCompareId } = useParams<ProgramStatusUrlParams>();
        // convert version hashes to string with comma between
        const hashcode = [id, versionToCompareId].filter((v): v is string => Boolean(v)).join(',');
        const { versionList: versionFromHashMap } = useHashmapConverter(hashcode);

        const latestVersionId = overrideLatestVersionId ?? versionFromHashMap[0]?.versionDbId;

        const [queryStringValue] = useQueryState('activityCodes');
        const { getValueFromStore } = useGlobalFilterDataStore();
        const storeValue = getValueFromStore<ISelectOption<Value>[]>('activityCodes');

        const { data: activityCodes } = useQueryActivityCodesByVersion({
            latestVersion: latestVersionId,
        });

        const options = useMemo<ISelectOption<Value>[]>(
            () =>
                activityCodes
                    ? orderBy(
                          activityCodes.map(activityCodeMapperUID),
                          [(item: any) => item['label'].toLowerCase()],
                          ['asc'],
                      )
                    : [],
            [activityCodes],
        );

        const isValue = useMemo(() => {
            if (queryStringValue && Array.isArray(queryStringValue) && queryStringValue.length > 0) {
                return queryStringValue.map((item) => {
                    return {
                        ...item,
                        value: {
                            ...item.value,
                            name: Number(item.value.name),
                            isExclude: item.value.isExclude === 'true',
                        },
                    };
                });
            } else {
                return storeValue;
            }
        }, [queryStringValue, storeValue]);

        useEffect(() => {
            if (setState) {
                if (!isValue || isValue.length === 0) {
                    setState((prev) => {
                        return produce(prev, (draft) => {
                            draft.activityCodes.isReady = true;
                            draft.activityCodes.isEmpty = true;
                            return draft;
                        });
                    });
                }
                if (isValue && isValue.length > 0 && !skipFormSet) {
                    methods?.setValue(name, isValue);
                    setState((prev) => {
                        return produce(prev, (draft) => {
                            draft.activityCodes.isReady = true;
                            return draft;
                        });
                    });
                }
            }
        }, [isValue, setState, methods, name]);

        const handleChange = (value: readonly ISelectOption<Value>[]) => {
            methods?.setValue(name, value);
            handleFilters && handleFilters();
        };

        return (
            <div className={classes.selectWrapper}>
                <div data-testid={'activity-codes-filter'} className={classes.activityCodesSelectWrapper}>
                    <Controller
                        render={({ field }) => {
                            return (
                                <Select<ISelectOption<Value>, true>
                                    {...field}
                                    options={options}
                                    isMulti
                                    components={{
                                        MenuList: MenuList,
                                        Option: OptionsWithExclude,
                                    }}
                                    isOptionDisabled={() => field.value?.length >= constants.MULTI_SELECT_MAX_ITEMS}
                                    value={field.value}
                                    onChange={handleChange}
                                    placeholder={'Select Activity Codes...'}
                                    label={FilterLabels.ACTIVITY_CODES}
                                />
                            );
                        }}
                        name={name}
                        control={methods?.control}
                    />
                </div>
            </div>
        );
    },
);
